import { Injectable, effect, inject } from '@angular/core';
import { DIR_DOCUMENT, Direction, Directionality } from '@angular/cdk/bidi';
import { LanguageService } from '@core/services/language.service';

/**
 * Directionality that follows LanguageService instead of reading `dir` once.
 *
 * Everything the CDK renders in an overlay — dialogs, select panels, menus,
 * tooltips — gets an explicit `dir` attribute stamped on its host from
 * Directionality.value at creation time. It does NOT inherit `dir` from <html>
 * through CSS, so an overlay is only mirrored if this service says rtl.
 *
 * The stock implementation reads document.documentElement.dir in its
 * constructor and never looks again. That breaks twice for us:
 *
 *   1. index.html ships `<html lang="en">` with no `dir`, and LanguageService
 *      only stamps it on when it is first injected. Whichever happens first
 *      wins, so a reload with the portal in Arabic can still leave every
 *      overlay ltr — the page mirrors, the dialog does not.
 *   2. Toggling the language at runtime updates <html dir> but not this value,
 *      so overlays keep the direction they were born with until a reload.
 *
 * Tracking the signal fixes both, and emitting `change` lets already-open
 * components (mat-tab-group's ink bar, overlay position strategies) re-mirror.
 */
@Injectable()
export class LanguageDirectionality extends Directionality {
  private readonly languageService = inject(LanguageService);

  constructor() {
    super(inject(DIR_DOCUMENT, { optional: true }));

    effect(() => {
      const direction = this.languageService.dir();
      if (direction === this.value) return;
      // `value` is readonly to consumers; this service is the one writer.
      (this as { value: Direction }).value = direction;
      this.change.emit(direction);
    });
  }
}
